import { useAuthContext } from "@/components/auth"

/**
 * Hook để kiểm tra quyền của người dùng
 * @returns Object chứa các function kiểm tra quyền
 */
export const usePermissions = () => {
  const auth = useAuthContext()
  const userPermissions = auth.permissions || []

  /**
   * Kiểm tra xem người dùng có quyền cụ thể hay không
   * @param permission - Tên quyền cần kiểm tra
   * @returns boolean - true nếu có quyền, false nếu không có
   */
  const hasPermission = (permission: string): boolean => {
    return userPermissions.includes(permission)
  }

  /**
   * Kiểm tra xem người dùng có ít nhất một trong các quyền được cung cấp hay không
   * @param permissions - Mảng các quyền cần kiểm tra
   * @returns boolean - true nếu có ít nhất một quyền, false nếu không có quyền nào
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(permission => userPermissions.includes(permission))
  }

  /**
   * Kiểm tra xem người dùng có tất cả các quyền được cung cấp hay không
   * @param permissions - Mảng các quyền cần kiểm tra
   * @returns boolean - true nếu có tất cả quyền, false nếu thiếu ít nhất một quyền
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(permission => userPermissions.includes(permission))
  }

  /**
   * Lấy danh sách quyền của người dùng
   * @returns string[] - Mảng các quyền của người dùng
   */
  const getUserPermissions = (): string[] => {
    return [...userPermissions]
  }

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getUserPermissions,
    userPermissions
  }
}

/**
 * Function kiểm tra quyền độc lập (không cần hook)
 * @param userPermissions - Mảng quyền của người dùng
 * @param permission - Quyền cần kiểm tra
 * @returns boolean
 */
export const checkPermission = (userPermissions: string[], permission: string): boolean => {
  return userPermissions.includes(permission)
}

/**
 * Function kiểm tra nhiều quyền độc lập
 * @param userPermissions - Mảng quyền của người dùng
 * @param permissions - Mảng quyền cần kiểm tra
 * @param requireAll - true: cần tất cả quyền, false: chỉ cần ít nhất một quyền
 * @returns boolean
 */
export const checkPermissions = (
  userPermissions: string[], 
  permissions: string[], 
  requireAll: boolean = false
): boolean => {
  if (requireAll) {
    return permissions.every(permission => userPermissions.includes(permission))
  }
  return permissions.some(permission => userPermissions.includes(permission))
} 